/**
 * UC3 — Agricultural Productivity Diagnosis, maize (design §3.3, Amendment 1).
 *
 * A stepper (`Stepper.tsx`) over machinery this codebase already has: the geography/scope
 * cascade, the catalogue's own raster epochs, and the `conditions` engine tool (Task 5)
 * submitted through the same `useToolRun` every §F6 dock form uses. Local state carries the
 * reader's choices — none of it is permalinked, the same rule `intel.*` is already under
 * (design §6.3) — seeded once from the store's own country/scope so a reader who has
 * already filtered the map to a country or a watershed opens the stepper already pointed
 * there, without this stepper ever writing back to those global controls (the same one-way
 * seeding `Change.tsx`'s `initialExtent` uses for its own extent pick).
 *
 * Amendment 1 §2.3/§3.3: the step list itself comes from `GET uc3/definition`'s own
 * `definition.steps` (order, ids, kinds) rather than a hardcoded array, so a future
 * definition edit (reordering, adding/removing a step) needs no frontend change — only the
 * `aeti` step is folded into the rainfall step as its own optional toggle (`#uc3Aeti`)
 * rather than consuming a step number of its own. `#uc3Step-<n>` still numbers the steps
 * 1..N in the order the definition lists them.
 *
 * UC3 launch updates (client, 8 Oct 2026): Year comes before Crop; the Geography step
 * isolates the map to the chosen country or watershed (`intel.isolate`, drawn and used as
 * the raster mask by `features/map/IntelIsolate.tsx`) and fits the view to it; the Crop and
 * Productivity steps put the crop's own layers on the map (`uc3Layers.ts`), with
 * `#uc3Measure` switching Productivity between the survey yield terciles and the modelled
 * GAEZ layers for reference only; the Vegetation step has no swipe; the Results panel
 * carries no limitations block.
 *
 * Production total and flow layers (client, 8 Oct 2026, NOTES #753): every painting step
 * adds its layer to `intel.uc3Layers` and leaves the earlier ones on the map (drawn by
 * `RasterLayers`/`ResultLayers`, toggled from the legend); changing a step's choice replaces
 * its layer in place. The Productivity step's `#uc3ProductionTotal` gives the selected
 * unit's modelled 2020 production (the diagnosis payload's `production.sums`) beside its
 * survey yield and tercile.
 *
 * One Relationships step and the crop production grid (client, 12 Oct 2026, NOTES #759):
 * the steps are Geography, Year and season, Crop, Productivity, Relationships, Analyse,
 * Results. The Vegetation, Rainfall and Soil steps are folded into Relationships (a stored
 * definition that still lists them, or `aeti`, is read without them: `FOLDED_STEPS`), whose
 * factor choice paints that factor's raster as the flow step `factor` before any run. The
 * Crop step shows the crop's GGCP10 production grid for the production year, and
 * `#uc3ProductionTotal` sums that grid inside the unit (`diagnosis.production`, thousand t
 * cells served in tonnes, printed "1.90 million t"). The year list runs from 2010 (the
 * grid's first year) to the newest season; production is read for the chosen year up to
 * 2020 and vegetation from the archive's first season for an earlier year, each with a note
 * (`uc3Layers.productionYear`/`seasonYear`, the frontend copy of `uc3_years`).
 *
 * Results replaces Analyse (client, 12 Oct 2026, NOTES #760): the condition builder step is
 * gone, so the steps are Geography, Year and season, Crop, Productivity, Relationships,
 * Results (a stored definition that still lists `conditions` reads without it,
 * `FOLDED_STEPS`). A finished Relationships run moves the reader to Results, which shows the
 * compare view and each run's card (`Uc3RelationshipsResults`), the production tiles of the
 * Productivity step, then the ranked watersheds, the country roll-up, the series and the
 * insight. The "Matching area" statistics and histogram went with the condition builder:
 * with no condition there is no matching area.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  fetchUnitDetail,
  fetchUnitOptions,
  fetchUnits,
  queryKeys,
  type IntelDiagnosis,
  type IntelSeries,
  type IntelStepDefinitionRow,
  type IntelUc3Crop,
} from '../../lib/api';
import { ChartFrame } from '../../components/charts/ChartFrame';
import { axisTitle } from '../../components/charts/scale';
import { productionFigure } from '../../lib/display';
import { fmtNum } from '../../lib/format';
import type { LbrFactor } from '../../lib/analysis';
import {
  COUNTRY_NAMES,
  PROGRAMME_BOUNDS,
  useAppStore,
  WATERSHED_LEVEL,
  type Country,
  type IntelIsolate,
} from '../../lib/state';
import { epochDate, epochsForYear } from '../map/epochs';
import { useAnalysisLayers } from '../analysis/pickers';
import { Insight } from './Insight';
import { Stepper, type StepperStep } from './Stepper';
import { RankedBarChart } from './charts/RankedBarChart';
import { StatTile, StatTiles } from './charts/StatTile';
import { SeriesChart } from './charts/SeriesChart';
import { useIntelDefinition, useIntelDiagnosis, useIntelSeries, useIntelUc3Config } from './api';
import { useUnitLink } from './useUnitLink';
import { Uc3RelationshipsResults, Uc3RelationshipsStep, type LbrSoilLayer } from './Uc3Relationships';
import { useUc3Relationships, type Uc3RelationshipsState } from './useUc3Relationships';
import {
  GGCP10_LAYER,
  PRODUCTION_YEARS,
  productionYear,
  rasterForStep,
  seasonYear,
  TERCILE_COLORS,
  TERCILES,
  UC3_MEASURES,
  yieldTerciles,
  type Uc3Measure,
} from './uc3Layers';

/** The four real countries the Geography step offers beside "All countries". */
const UC3_COUNTRIES = (Object.keys(COUNTRY_NAMES) as Country[]).filter((c) => c !== 'ALL');

/** The productivity map's own result layer id. */
const PRODUCTIVITY_LAYER = 'intel:uc3:productivity';

/**
 * The flow steps whose layers this component writes (the Relationships runs write their
 * own). `factor` is the Relationships step's factor raster, painted while that
 * step is open (`paintingStepOf`).
 */
const PAINTING_STEPS = ['crop', 'indicator', 'factor'] as const;

/** The flow step a stepper step paints into, if any. */
const paintingStepOf = (stepId: string): string | null =>
  stepId === 'relationships' ? 'factor' : (PAINTING_STEPS as readonly string[]).includes(stepId) ? stepId : null;

/**
 * Step ids a stored definition may still carry from before 12 Oct 2026; their choices now
 * live in the Relationships step (NOTES #759), and `conditions` (Analyse) is gone, its
 * results read at Results (NOTES #760), so the stepper does not number them.
 */
const FOLDED_STEPS = ['ndvi', 'rainfall', 'aeti', 'soil', 'conditions'];

/** The year list's first year when the definition names none: GGCP10's first year. */
const FIRST_YEAR = 2010;

const DIAGNOSIS_FACTORS = ['ndvi_peak', 'rain', 'soc'] as const;

/**
 * `uc3/diagnosis`'s own factor units, presentation standard §1's "numbers with unit". Since
 * Part C (11 Oct 2026) the payload names them itself (`diagnosis.units`, from the layers'
 * display contracts) and this is the fallback for an older payload.
 */
const DIAGNOSIS_UNITS: Record<'yield' | 'modelled_yield' | (typeof DIAGNOSIS_FACTORS)[number], string> = {
  yield: 'kg/ha',
  modelled_yield: 't/ha',
  ndvi_peak: 'NDVI',
  rain: 'mm',
  soc: 'g/kg',
};

/**
 * GAEZ round: `uc3/definition`'s own `definition.crops`/`uc3/config`'s own `crops` — kept in
 * sync server-side (Task 2's own report) — used before either has resolved, the same role
 * `FALLBACK_STEPS` plays for the step list.
 */
const FALLBACK_CROPS: IntelUc3Crop[] = [
  { id: 'maize', label: { en: 'Maize', fr: 'Maïs' }, survey: 'mb_yield_maize', mask: 'mask_maize', gaez: 'gaez_yld_maize' },
  { id: 'rice', label: { en: 'Rice', fr: 'Riz' }, survey: 'mb_yield_rice', mask: 'mask_rice', gaez: 'gaez_yld_rice' },
  { id: 'soy', label: { en: 'Soybean', fr: 'Soja' }, survey: 'mb_yield_soybean', mask: 'mask_soy', gaez: 'gaez_yld_soy' },
  { id: 'sorghum', label: { en: 'Sorghum', fr: 'Sorgho' }, survey: 'mb_yield_sorghum', mask: null, gaez: 'gaez_yld_sorghum' },
  { id: 'millet', label: { en: 'Millet', fr: 'Mil' }, survey: 'mb_yield_millet', mask: null, gaez: 'gaez_yld_millet' },
];

/**
 * Fix round 1: a defensive display filter — no ρ/correlation term reaches the reader even
 * from a stale cache or a payload a future backend round mis-shapes, on top of the design
 * guarantee that the server never sends one. Matches the same four terms
 * `vocabulary.test.ts` sweeps the locale files for.
 */
function hasForbiddenStatsTerm(text: string): boolean {
  if (text.includes('ρ')) return true;
  return /\b(rho|correlation|corrélation)\b/i.test(text);
}

/** Drops any statement line carrying a forbidden term, warning once per dropped line. */
function safeStatements(lines: string[] | undefined): string[] {
  if (!lines) return [];
  return lines.filter((line) => {
    if (hasForbiddenStatsTerm(line)) {
      console.warn('[uc3] dropped a diagnosis statement carrying a forbidden correlation term', line);
      return false;
    }
    return true;
  });
}

/** Same filter for the single insight paragraph, per language, falling back to the empty state. */
function safeInsightText(text: string | undefined, fallback: string): string {
  if (!text) return fallback;
  if (hasForbiddenStatsTerm(text)) {
    console.warn('[uc3] dropped a diagnosis insight carrying a forbidden correlation term', text);
    return fallback;
  }
  return text;
}

/**
 * The stepper's own fallback order — used only while `uc3/definition` has not resolved yet,
 * so the pane still renders something on first paint. Identical in shape to the live
 * `uc3/definition.steps` (NOTES #760: six steps, Relationships fifth, Results last).
 */
const FALLBACK_STEPS: IntelStepDefinitionRow[] = [
  { id: 'geography', kind: 'geography', binding: { levels: ['admin0', 'watershed'] } },
  { id: 'year', kind: 'year', binding: { first: FIRST_YEAR } },
  { id: 'crop', kind: 'crop', binding: {} },
  { id: 'indicator', kind: 'indicator', binding: { indicator: 'mb_yield_maize' } },
  {
    id: 'relationships',
    kind: 'relationships',
    binding: {
      ndvi_layer: 'ndvi_terra',
      rainfall: { policy: 'dekads_else_annual', datasets: { dekad: 'chirps', annual: 'chirpsy' } },
      years: [...PRODUCTION_YEARS],
    },
  },
  { id: 'results', kind: 'results', binding: { ranked: true, correlations: false } },
];

function uniqSorted(values: number[]): number[] {
  return [...new Set(values.filter((v) => Number.isFinite(v)))].sort((a, b) => a - b);
}

function yearOf(date: string): number {
  return Number(date.slice(0, 4));
}

function langOf(language: string): 'en' | 'fr' {
  return language === 'fr' ? 'fr' : 'en';
}

export function Uc3() {
  const { t, i18n } = useTranslation();
  const lang = langOf(i18n.language);
  const step = useAppStore((s) => s.intel.step);
  const setStep = useAppStore((s) => s.setIntelStep);
  const storeCountry = useAppStore((s) => s.country);
  const storeScope = useAppStore((s) => s.scope);
  const layerOpacity = useAppStore((s) => s.layerOpacity);
  const setLayerOpacity = useAppStore((s) => s.setLayerOpacity);
  const setEpoch = useAppStore((s) => s.setEpoch);
  const addResultLayer = useAppStore((s) => s.addResultLayer);
  const removeResultLayer = useAppStore((s) => s.removeResultLayer);
  const setIntelIsolate = useAppStore((s) => s.setIntelIsolate);
  const flowLayers = useAppStore((s) => s.intel.uc3Layers);
  const setUc3StepLayers = useAppStore((s) => s.setUc3StepLayers);
  const clearUc3Layers = useAppStore((s) => s.clearUc3Layers);
  const setReportingLevel = useAppStore((s) => s.setReportingLevel);
  const fitBounds = useAppStore((s) => s.fitBounds);
  const qc = useQueryClient();

  // The productivity map paints watersheds, so the map's units are the watershed ones
  // (the same assertion UC1 and UC2 make for their own level).
  useEffect(() => {
    setReportingLevel(WATERSHED_LEVEL);
  }, [setReportingLevel]);

  useEffect(() => {
    if (step === 0) setStep(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const active = step || 1;

  const definitionQuery = useIntelDefinition('uc3');
  const allStepDefs = definitionQuery.data?.definition.steps ?? FALLBACK_STEPS;
  // NOTES #759: the old Vegetation/Rainfall/Soil steps (and `aeti`) are folded into Relationships.
  const stepDefs = useMemo(() => allStepDefs.filter((s) => !FOLDED_STEPS.includes(s.id)), [allStepDefs]);
  const stepNumberOf = (id: string): number => {
    const i = stepDefs.findIndex((s) => s.id === id);
    return i === -1 ? 0 : i + 1;
  };
  const relationshipsDef = stepDefs.find((s) => s.id === 'relationships');
  const yearDef = stepDefs.find((s) => s.id === 'year');
  // The rainfall datasets: the Relationships binding, else an older definition's Rainfall step.
  const rainfallDatasets =
    relationshipsDef?.binding.rainfall?.datasets ?? allStepDefs.find((s) => s.id === 'rainfall')?.binding.datasets;
  const bindingYears = relationshipsDef?.binding.years;
  const productionRange = useMemo<readonly [number, number]>(
    () => (bindingYears?.length === 2 ? [bindingYears[0]!, bindingYears[1]!] : PRODUCTION_YEARS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bindingYears?.join(',')],
  );

  // `'ALL'` is "All countries": the whole programme, nothing isolated on the map.
  const [country, setCountry] = useState<Country>(() => storeCountry);
  const [scopeUnit, setScopeUnit] = useState<string>(() =>
    storeScope?.unitType === 'watershed' ? storeScope.unitId : '',
  );

  const watersheds = useQuery({
    queryKey: ['uc3-watersheds', country],
    queryFn: () => fetchUnitOptions('watershed', null, country as Country, null, false),
  });

  /* ---- geography: isolate the map to the selection and fit to it ---- */
  // The four level-0 outlines, on `GeographyFocus`'s own cache key: the country's unit id.
  const admin0 = useQuery({
    queryKey: queryKeys.units('admin', 0, 'ALL', 'z7'),
    queryFn: () => fetchUnits('admin', 0, 'ALL', 'z7'),
  });
  const countryUnitId =
    country === 'ALL' ? null : (admin0.data?.features.find((f) => f.properties.country === country)?.properties.id ?? null);
  const isolate: IntelIsolate | null = scopeUnit
    ? { unitId: scopeUnit, level: 'watershed', country }
    : country !== 'ALL'
      ? { unitId: countryUnitId ?? country, level: 'admin0', country }
      : null;
  useEffect(() => {
    setIntelIsolate(isolate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isolate?.unitId, isolate?.level, isolate?.country]);
  useEffect(() => () => setIntelIsolate(null), [setIntelIsolate]);

  /**
   * The same fit `#intelFocus` makes through `useUnitLink.pick`: the unit's own
   * `/hierarchy/units/{id}.bbox`, handed to `fitBounds`. Only on a *change* of geography,
   * never on opening the stepper (the map is already where the reader left it), and back
   * to the programme extent when the selection is cleared.
   */
  const geographyKey = `${country}|${scopeUnit}`;
  const fittedGeography = useRef(geographyKey);
  useEffect(() => {
    if (fittedGeography.current === geographyKey) return;
    const target = scopeUnit || countryUnitId;
    if (!target) {
      if (country === 'ALL') {
        fittedGeography.current = geographyKey;
        fitBounds(PROGRAMME_BOUNDS);
      }
      return;
    }
    fittedGeography.current = geographyKey;
    void qc
      .fetchQuery({ queryKey: queryKeys.unit(target), queryFn: () => fetchUnitDetail(target) })
      .then((detail) => {
        if (fittedGeography.current !== geographyKey || !detail.bbox) return;
        const [minx, miny, maxx, maxy] = detail.bbox;
        fitBounds([
          [miny, minx],
          [maxy, maxx],
        ]);
      })
      .catch(() => {
        /* no geometry for this unit: the isolation still stands, only the fit does not fire */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geographyKey, countryUnitId]);

  const config = useIntelUc3Config();
  /**
   * GAEZ round: `definition.crops` is the ruling's own source for the crop select — falls
   * back to `uc3/config`'s own `crops` (kept in sync server-side) while the definition has
   * not resolved yet, then to the hardcoded fallback.
   */
  const crops = definitionQuery.data?.definition.crops ?? config.data?.crops ?? FALLBACK_CROPS;
  const [crop, setCrop] = useState<string>('maize');
  useEffect(() => {
    if (crops.length && !crops.some((c) => c.id === crop)) setCrop(crops[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definitionQuery.data, config.data]);
  const cropDef = crops.find((c) => c.id === crop) ?? crops[0];
  const cropLabel = cropDef ? cropDef.label[lang] : crop;

  const layers = useAnalysisLayers();
  const ndviSeries = layers.find((l) => l.id === 'ndvi_terra') ?? null;
  const ndviYears = useMemo(
    () => uniqSorted((ndviSeries?.epochs ?? []).map((e) => yearOf(epochDate(e)))),
    [ndviSeries],
  );

  /* ---- rainfall step: dekad (`chirps`) when the season's dekads exist, else annual (`chirpsy`) ---- */
  const dekadLayerId = rainfallDatasets?.dekad ?? 'chirps';
  const annualLayerId = rainfallDatasets?.annual ?? 'chirpsy';
  const dekadSeries = layers.find((l) => l.id === dekadLayerId) ?? null;
  const annualSeries = layers.find((l) => l.id === annualLayerId) ?? null;
  const dekadYears = useMemo(
    () => uniqSorted((dekadSeries?.epochs ?? []).map((e) => yearOf(epochDate(e)))),
    [dekadSeries],
  );
  const annualYears = useMemo(
    () => uniqSorted((annualSeries?.epochs ?? []).map((e) => yearOf(epochDate(e)))),
    [annualSeries],
  );

  /**
   * Years both `ndvi_terra` and the annual rainfall layer can answer for — falling back to
   * `ndvi_terra` alone when the rainfall archive has published none yet or the two series
   * simply do not overlap. Since NOTES #759 this only picks the default year; the list
   * itself (`yearOptions`) runs from 2010.
   */
  const seasonYears = useMemo(() => {
    if (!annualYears.length) return ndviYears;
    const intersection = ndviYears.filter((y) => annualYears.includes(y));
    return intersection.length ? intersection : ndviYears;
  }, [ndviYears, annualYears]);
  /**
   * NOTES #759: every year from the grid's first (2010, `binding.first`) to the newest
   * season the archives hold (2020 at the least, the grid's last year).
   */
  const firstYear = typeof yearDef?.binding.first === 'number' ? yearDef.binding.first : FIRST_YEAR;
  const yearOptions = useMemo(() => {
    const last = Math.max(productionRange[1], ndviYears[ndviYears.length - 1] ?? 0, annualYears[annualYears.length - 1] ?? 0);
    return Array.from({ length: Math.max(0, last - firstYear + 1) }, (_, i) => firstYear + i);
  }, [firstYear, productionRange, ndviYears, annualYears]);
  const [year, setYear] = useState<number | null>(null);
  useEffect(() => {
    // Not before the catalogue has landed: the default is the newest season, which the
    // catalogue's epochs name (an earlier pick would fetch the diagnosis twice).
    if (!layers.length) return;
    if (yearOptions.length && (year === null || !yearOptions.includes(year))) {
      const preferred = seasonYears[seasonYears.length - 1];
      setYear(preferred !== undefined && yearOptions.includes(preferred) ? preferred : yearOptions[yearOptions.length - 1]!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearOptions.join(','), seasonYears.join(','), layers.length]);
  /** The year each source reads (NOTES #759): production up to 2020, vegetation from the archive's start. */
  const prodYear = year === null ? null : productionYear(year, productionRange);
  const vegYear = year === null ? null : seasonYear(year, ndviYears[0]);

  // "season dekads when the archive holds the season, else the annual layer" — approximated
  // here as "the dekad layer has published at least one epoch for the chosen year", the same
  // year-level granularity every other epoch check in this stepper already uses.
  const usesDekads = year !== null && dekadYears.includes(year);
  const rainfallLayerId = usesDekads ? dekadLayerId : annualLayerId;
  const rainfallSeries = usesDekads ? dekadSeries : annualSeries;

  /* ---- relationships step: the factor, its soil variable and its layer (NOTES #759) ---- */
  const [factor, setFactor] = useState<LbrFactor>('ndvi');
  const [soilLayer, setSoilLayer] = useState<LbrSoilLayer>('soc');
  const ndviLayerId = relationshipsDef?.binding.ndvi_layer ?? 'ndvi_terra';

  /* ---- productivity step: survey yield terciles per watershed, or a modelled layer ---- */
  // `#uc3Measure` is presentation only: the analysis and the results always read the
  // survey yield, whatever this select says.
  const [measure, setMeasure] = useState<Uc3Measure>('survey');

  const activeStepId = stepDefs[active - 1]?.id ?? '';

  /* ---- relationships step: Local Bivariate Relationships runs (Part B, 11 Oct 2026) ---- */
  const relationships = useUc3Relationships();
  const methodology = definitionQuery.data?.definition.methodology as
    | { steps?: Record<string, string[]>; limitations_text?: Record<string, string> }
    | undefined;
  const relationshipsMethod = methodology?.steps?.[lang]?.length
    ? { steps: methodology.steps[lang]!, limitations: methodology.limitations_text?.[lang] }
    : undefined;

  /* ---- Results (NOTES #760): a finished Relationships run opens it ---- */
  const resultsStepNum = stepNumberOf('results');
  const lastRunCount = useRef(0);
  useEffect(() => {
    const newest = relationships.runs[relationships.runs.length - 1]?.n ?? 0;
    if (newest > lastRunCount.current && resultsStepNum) setStep(resultsStepNum);
    lastRunCount.current = newest;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relationships.runs]);

  /**
   * Fix round 1: the results step's selected unit is `useUnitLink('uc3')`'s own `unit` — the
   * same map-click-selects-it wiring `Uc1`/`Uc2` already use — rather than local state of
   * its own, so clicking a unit *on the map* (a watershed the productivity terciles paint)
   * reaches this pane exactly as picking one from a chart does.
   */
  const { unit: selectedUnit, pick: pickUnit } = useUnitLink('uc3');
  /**
   * Fix wave (launch intelligence, F3c): `uc3/diagnosis` is the whole-programme ranked view
   * design §3.3 describes, not the stepper's own country/scope pick, which scopes the
   * Relationships runs and the production totals. Every one of this dev stack's four countries has
   * 2-3 watersheds on its own — scoping the diagnosis to one country would starve the
   * ranking, regardless of which country or watershed the reader picked in step 1.
   */
  const diagnosis = useIntelDiagnosis(crop, year, null);
  const series = useIntelSeries(selectedUnit, vegYear);

  const terciles = useMemo(() => yieldTerciles(diagnosis.data?.ranked ?? []), [diagnosis.data]);

  /* ---- flow layers (client, 8 Oct 2026, NOTES #753) ---- */
  /**
   * What each painting step puts on the map for the current choices (`uc3Layers.ts`):
   * `rasterForStep` for the crop, vegetation, rainfall and soil rasters and the modelled
   * productivity measures; the survey measure is the tercile choropleth `PRODUCTIVITY_LAYER`
   * (once the diagnosis has ranked something). The Relationships runs' layers are written by
   * `useUc3Relationships`, not here.
   */
  const layerName = (id: string): string => layers.find((l) => l.id === id)?.name ?? id;
  const desiredFor = (stepId: string): { layerId: string; label: string }[] | null => {
    if (stepId === 'indicator' && measure === 'survey') {
      return terciles.size ? [{ layerId: PRODUCTIVITY_LAYER, label: t('intel.uc3.layer.yield', { crop: cropLabel }) }] : null;
    }
    const choice = rasterForStep(stepId, {
      crop: cropDef,
      measure,
      ndviLayerId,
      rainfallLayerId,
      soilLayer,
      factor,
      productionYear: prodYear ?? undefined,
    });
    if (!choice) return null;
    // The factor layer names its year: the vegetation season, or the rainfall year.
    const factorYear = factor === 'ndvi' ? vegYear : factor === 'rain' ? year : null;
    const label = choice.titleKey
      ? t(choice.titleKey, { crop: cropLabel, year: prodYear })
      : `${layerName(choice.layerId)}${factorYear ? `, ${factorYear}` : ''}`;
    return [{ layerId: choice.layerId, label }];
  };
  const desiredKey = JSON.stringify(PAINTING_STEPS.map((id) => desiredFor(id)));
  /**
   * Entering a painting step adds its layer; every step already on the list follows its
   * own choice (another crop replaces the crop layer and the productivity layer in place).
   * A layer the reader removed with the legend's ✕ stays off until its step is entered
   * again: `entered` only holds steps entered since their layer was last on the list.
   */
  const entered = useRef(new Set<string>());
  const lastActiveStep = useRef<string | null>(null);
  useEffect(() => {
    if (lastActiveStep.current !== activeStepId) {
      lastActiveStep.current = activeStepId;
      const painting = paintingStepOf(activeStepId);
      if (painting) entered.current.add(painting);
    }
    const listed = new Set(useAppStore.getState().intel.uc3Layers.map((l) => l.stepId));
    for (const stepId of PAINTING_STEPS) {
      if (!listed.has(stepId) && !entered.current.has(stepId)) continue;
      const desired = desiredFor(stepId);
      if (!desired) continue;
      setUc3StepLayers(stepId, desired);
      entered.current.delete(stepId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepId, desiredKey]);

  /**
   * The factor layer shows its year's latest epoch: the vegetation season's last composite
   * (NOTES #759: the archive's first season for an earlier year), or the rainfall year's
   * last dekad or its annual total. Soil is static.
   */
  const factorEntry = flowLayers.find((l) => l.stepId === 'factor');
  const factorSeries =
    factorEntry?.layerId === ndviLayerId ? ndviSeries : factorEntry?.layerId === rainfallLayerId ? rainfallSeries : null;
  const factorEpochYear = factorEntry?.layerId === ndviLayerId ? vegYear : year;
  useEffect(() => {
    if (!factorEntry || !factorSeries?.epochs?.length) return;
    const inWindow = epochsForYear(factorSeries, factorEpochYear);
    const target = inWindow[inWindow.length - 1] ?? factorSeries.epochs[factorSeries.epochs.length - 1];
    if (target) setEpoch(factorEntry.layerId, epochDate(target));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factorEntry?.layerId, factorSeries, factorEpochYear]);

  /**
   * The productivity map: survey yield terciles (rank thirds of the programme's ranked
   * watersheds, `yieldTerciles`) from the diagnosis payload the results already read, as a
   * clickable `units` layer with its own class legend. On the map for as long as the flow
   * list names it; `ResultLayers` keeps the watersheds inside the isolated geography.
   */
  const productivityListed = flowLayers.some((l) => l.layerId === PRODUCTIVITY_LAYER);
  useEffect(() => {
    if (!productivityListed || !terciles.size) return;
    const unitIds = [...terciles.keys()];
    addResultLayer(
      {
        id: PRODUCTIVITY_LAYER,
        kind: 'units',
        label: t('intel.uc3.layer.yield', { crop: cropLabel }),
        unit_ids: unitIds,
        colors: Object.fromEntries(unitIds.map((id) => [id, TERCILE_COLORS[terciles.get(id)!]])),
        details: Object.fromEntries(unitIds.map((id) => [id, t(`intel.uc3.tercile.${terciles.get(id)!}`)])),
        classes: TERCILES.map((c) => ({ label: t(`intel.uc3.tercile.${c}`), color: TERCILE_COLORS[c] })),
        // Translucent so the flow's rasters beneath (crop area, vegetation, rainfall) stay
        // readable while the classes are checked in the legend.
        fillOpacity: 0.45,
      },
      { jobId: `uc3-yield-${year}-${crop}`, tool: 'uc3' },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productivityListed, terciles, cropLabel, lang]);

  // Leaving the use case (or closing the dock) clears the flow and the layers it named.
  useEffect(
    () => () => {
      clearUc3Layers();
      removeResultLayer(PRODUCTIVITY_LAYER);
    },
    [clearUc3Layers, removeResultLayer],
  );

  /* ---- the Productivity step's totals for the selected unit (`#uc3ProductionTotal`) ---- */
  const productionUnitId = scopeUnit || (country !== 'ALL' ? countryUnitId : null);
  const production = diagnosis.data?.production;
  const productionSums = production?.sums;
  const productionTotal = productionUnitId && productionSums ? (productionSums[productionUnitId] ?? null) : null;
  const productionShown = productionTotal != null ? productionFigure(productionTotal, lang) : null;
  const productionNote = production?.note?.[lang] ?? null;
  // GAEZ v5 2020, shown beside the grid total only with the Modelled measure.
  const modelledSums = diagnosis.data?.production_modelled?.sums;
  const modelledTotal = productionUnitId && modelledSums ? (modelledSums[productionUnitId] ?? null) : null;
  const modelledShown = modelledTotal != null ? productionFigure(modelledTotal, lang) : null;
  const productionCaption =
    production?.source === 'ggcp10'
      ? t('intel.uc3.production.gridCaption', { year: production.year })
      : t('intel.uc3.production.modelledCaption');
  /**
   * Client, 8 Oct 2026 (NOTES #755): at watershed level the survey yield tile is that
   * watershed's own value and tercile class. At country level it is the SUM of the
   * country's watersheds' survey yield values (those that carry one), at the client's
   * instruction, labelled as a country total with no tercile class. `ranked` is the whole
   * programme's watersheds with a survey yield, so it carries every value the sum needs.
   */
  const unitYieldValues = (diagnosis.data?.ranked ?? [])
    .filter((r) => (scopeUnit ? r.unit_id === scopeUnit : r.country === country))
    .map((r) => r.yield)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  const unitYieldKg = unitYieldValues.length ? unitYieldValues.reduce((a, b) => a + b, 0) : null;
  const unitYieldClass = scopeUnit ? (terciles.get(scopeUnit) ?? null) : null;

  const onSelectUnit = (id: string) => {
    void pickUnit(id, diagnosis.data?.ranked.find((p) => p.unit_id === id)?.name);
  };

  const countryName =
    country === 'ALL' ? t('intel.focusAllCountries') : t(`country.${country}`, { defaultValue: COUNTRY_NAMES[country] });
  const scopeName = watersheds.data?.find((u) => u.id === scopeUnit)?.name;

  /** A watershed pick, from the select or the map: its own country comes with it. */
  const chooseWatershed = (id: string) => {
    setScopeUnit(id);
    const own = watersheds.data?.find((u) => u.id === id)?.country;
    if (id && own && own !== country && (UC3_COUNTRIES as string[]).includes(own)) setCountry(own as Country);
  };

  // The map stays clickable for re-selection: on the Geography step, a click that resolves
  // to one of the listed watersheds (`useUnitLink`) becomes the selection.
  const geographyStepNum = stepNumberOf('geography');
  useEffect(() => {
    if (active !== geographyStepNum || !selectedUnit || selectedUnit === scopeUnit) return;
    if (watersheds.data?.some((u) => u.id === selectedUnit)) chooseWatershed(selectedUnit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnit]);

  /**
   * The Productivity step's tiles for the selected unit, shown again at Results (NOTES #760:
   * the production tiles summary beside the Relationships runs).
   */
  const productionBlock: ReactNode = productionUnitId ? (
    <div className="result-box" id="uc3ProductionTotal">
      <h5>{t('intel.uc3.production.title', { crop: cropLabel, unit: scopeName ?? countryName })}</h5>
      <div className="result-pad">
        <StatTiles>
          <StatTile
            id="uc3-production-total"
            value={
              productionShown
                ? productionShown.value
                : diagnosis.isLoading
                  ? '-'
                  : t('intel.uc3.production.notAvailable')
            }
            unit={productionShown?.unit}
            text={productionTotal == null && !diagnosis.isLoading}
            label={t('intel.uc3.production.total')}
            sub={productionCaption}
          />
          {measure === 'modelled' && production?.source === 'ggcp10' ? (
            <StatTile
              id="uc3-production-modelled"
              value={modelledShown ? modelledShown.value : t('intel.uc3.production.notAvailable')}
              unit={modelledShown?.unit}
              text={!modelledShown}
              label={t('intel.uc3.production.modelledTotal')}
              sub={t('intel.uc3.production.modelledCaption')}
            />
          ) : null}
          <StatTile
            id="uc3-production-yield"
            value={
              unitYieldKg != null
                ? fmtNum(unitYieldKg / 1000, 2)
                : diagnosis.isLoading
                  ? '-'
                  : t('intel.uc3.production.notAvailable')
            }
            unit={unitYieldKg != null ? 't/ha' : undefined}
            text={unitYieldKg == null && !diagnosis.isLoading}
            label={scopeUnit ? t('intel.uc3.production.yield') : t('intel.uc3.production.yieldCountryTotal')}
            sub={
              scopeUnit
                ? unitYieldClass
                  ? t(`intel.uc3.production.tercile.${unitYieldClass}`)
                  : undefined
                : unitYieldValues.length
                  ? t('intel.uc3.production.yieldSumCaption', { n: fmtNum(unitYieldValues.length, 0) })
                  : undefined
            }
          />
        </StatTiles>
        {productionNote ? (
          <p className="note" id="uc3ProductionNote">
            {productionNote}
          </p>
        ) : null}
      </div>
    </div>
  ) : null;

  /* ---- one body renderer per step kind, keyed by the definition's own step id ---- */
  const bodyFor = (def: IntelStepDefinitionRow, n: number): { title: string; summary: ReactNode; body: ReactNode } => {
    switch (def.id) {
      case 'geography':
        return {
          title: t('intel.uc3.step.geography'),
          summary: `${countryName}${scopeName ? `, ${scopeName}` : ''}`,
          body: (
            <div className="ad-checks">
              <label>
                {t('indicators.levelCountry')}
                <select
                  id="uc3Country"
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value as Country);
                    setScopeUnit('');
                  }}
                >
                  <option value="ALL">{t('intel.focusAllCountries')}</option>
                  {UC3_COUNTRIES.map((c) => (
                    <option key={c} value={c}>
                      {t(`country.${c}`, { defaultValue: COUNTRY_NAMES[c] })}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t('intel.uc3.watershedLabel')}
                <select id="uc3Watershed" value={scopeUnit} onChange={(e) => chooseWatershed(e.target.value)}>
                  <option value="">
                    {country === 'ALL' ? t('intel.focusAllWatersheds') : t('intel.uc3.wholeCountry')}
                  </option>
                  {(watersheds.data ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'crop':
        return {
          title: t('intel.uc3.step.crop'),
          summary: cropLabel,
          body: (
            <div className="ad-checks">
              <select id="uc3Crop" value={crop} onChange={(e) => setCrop(e.target.value)}>
                {crops.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label[lang] ?? c.id}
                  </option>
                ))}
              </select>
              {GGCP10_LAYER[crop] ? (
                prodYear !== null && year !== null && prodYear !== year ? (
                  <p className="note" id="uc3CropGridNote">
                    {t('intel.uc3.cropGridYear', { year: prodYear })}
                  </p>
                ) : null
              ) : (
                <p className="note" id="uc3CropNoGrid">
                  {t('intel.uc3.cropNoGrid')}
                </p>
              )}
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'year':
        return {
          title: t('intel.uc3.step.year'),
          summary: year ?? '-',
          body: (
            <div className="ad-checks">
              <select id="uc3Year" value={year ?? ''} onChange={(e) => setYear(Number(e.target.value))}>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'indicator':
        return {
          title: t('intel.uc3.step.productivity'),
          summary: t('intel.uc3.productivityLabel', { crop: cropLabel }),
          body: (
            <div className="ad-checks">
              <p className="note">{t('intel.uc3.productivityNote')}</p>
              <label>
                {t('intel.uc3.measureLabel')}
                <select id="uc3Measure" value={measure} onChange={(e) => setMeasure(e.target.value as Uc3Measure)}>
                  {UC3_MEASURES.map((m) => (
                    <option key={m} value={m}>
                      {t(`intel.uc3.measure.${m}`)}
                    </option>
                  ))}
                </select>
              </label>
              {measure !== 'survey' ? <p className="note">{t('intel.uc3.measureReference')}</p> : null}
              {productionBlock}
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </div>
          ),
        };
      case 'relationships':
        return {
          title: t('intel.uc3.step.relationships'),
          summary: relationships.runs.length
            ? relationships.runs.map((r) => t(`intel.uc3.lbr.factor.${r.result.factor}`)).join(', ')
            : t(`intel.uc3.lbr.factor.${factor}`),
          body: (
            <>
              <Uc3RelationshipsStep
                state={relationships}
                unitId={productionUnitId}
                crop={crop}
                cropLabel={cropLabel}
                year={year}
                factor={factor}
                onFactor={setFactor}
                soilLayer={soilLayer}
                onSoil={setSoilLayer}
                opacity={factorEntry ? (layerOpacity[factorEntry.layerId] ?? 1) : 1}
                onOpacity={(value) => factorEntry && setLayerOpacity(factorEntry.layerId, value)}
                seasonNote={
                  vegYear !== null && year !== null && vegYear !== year
                    ? t('intel.uc3.seasonNote', { year: vegYear })
                    : null
                }
                rainNote={t(usesDekads ? 'intel.uc3.rainfallCaptionDekads' : 'intel.uc3.rainfallCaptionAnnual')}
                method={relationshipsMethod}
              />
              <button type="button" className="btn primary sm" onClick={() => setStep(n + 1)}>
                {t('intel.uc3.next')}
              </button>
            </>
          ),
        };
      case 'results':
      default:
        return {
          title: t('intel.uc3.step.results'),
          summary: undefined,
          body: (
            <Uc3Results
              relationships={relationships}
              production={productionBlock}
              diagnosis={diagnosis.data}
              series={series.data}
              selectedUnit={selectedUnit}
              onSelectUnit={onSelectUnit}
              lang={lang}
              cropLabel={cropLabel}
            />
          ),
        };
    }
  };

  const steps: StepperStep[] = stepDefs.map((def, i) => {
    const n = i + 1;
    const { title, summary, body } = bodyFor(def, n);
    return { id: n, title, summary, body };
  });

  return (
    <div className="intel-uc3">
      <Stepper active={active} onSelect={setStep} steps={steps} />
    </div>
  );
}

function Uc3Results({
  relationships,
  production,
  diagnosis,
  series,
  selectedUnit,
  onSelectUnit,
  lang,
  cropLabel,
}: {
  relationships: Uc3RelationshipsState;
  production: ReactNode;
  diagnosis?: IntelDiagnosis;
  series?: IntelSeries;
  selectedUnit: string | null;
  onSelectUnit: (id: string) => void;
  lang: 'en' | 'fr';
  cropLabel: string;
}) {
  const { t } = useTranslation();
  // Part C: the payload's own units win; the constants cover an older payload.
  const units = { ...DIAGNOSIS_UNITS, ...(diagnosis?.units ?? {}) };

  // Fix round 1: every read below is guarded — a stale cache entry, a partial payload, or
  // the pre-Task-4 `points`/`correlations` shape a test can still hand this component must
  // fall through to the designed empty state rather than throwing.
  const rankedRows = diagnosis?.ranked ?? [];
  const countryRows = diagnosis?.countries ?? [];
  const statementLines = safeStatements(diagnosis?.statements?.[lang]);
  const point = rankedRows.find((p) => p.unit_id === selectedUnit);
  const meanOf = (factor: (typeof DIAGNOSIS_FACTORS)[number] | 'yield'): number | null => {
    const vals = rankedRows.map((p) => p[factor]).filter((v): v is number => typeof v === 'number');
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };

  return (
    <div>
      <Uc3RelationshipsResults state={relationships} lang={lang} />
      {production}

      {/*
       * Amendment 1 §3.3: no correlation coefficient anywhere — the ranked bar chart and the
       * server's own rank-based `statements[]` replace the old scatter/ρ block outright.
       * Always rendered in the results step, same "designed empty state, never a silently
       * missing chart" rule the old block followed.
       */}
      <div className="result-box" id="uc3Ranked">
        <h5>{t('intel.uc3.rankedTitle')}</h5>
        <div className="result-pad">
          {rankedRows.length ? (
            <>
              {diagnosis?.season && diagnosis.year ? (
                <p className="sub">{t('intel.uc3.rankedPeriod', { season: diagnosis.season, year: diagnosis.year })}</p>
              ) : null}
              <ChartFrame
                title={t('intel.uc3.rankedTitle')}
                subtitle={diagnosis?.season && diagnosis.year ? t('intel.uc3.rankedPeriod', { season: diagnosis.season, year: diagnosis.year }) : undefined}
                xLabel={axisTitle(t('intel.charts.yield', { crop: cropLabel }), units.yield)}
                yLabel={t('charts.axis.watershed')}
                testId="uc3-ranked"
              >
                {() => (
                  <RankedBarChart
                    rows={rankedRows}
                    season={diagnosis?.season ?? ''}
                    year={diagnosis?.year ?? 0}
                    yieldLabel={t('intel.charts.yield', { crop: cropLabel })}
                    modelledLabel={t('intel.uc3.modelledColumnLabel')}
                    ndviLabel={t('intel.charts.factor.ndvi_peak')}
                    rainLabel={t('intel.charts.factor.rain')}
                    socLabel={t('intel.charts.factor.soc')}
                    yieldUnit={units.yield}
                    modelledUnit={units.modelled_yield}
                    ndviUnit={units.ndvi_peak}
                    rainUnit={units.rain}
                    socUnit={units.soc}
                    selectedId={selectedUnit}
                    onSelect={onSelectUnit}
                  />
                )}
              </ChartFrame>
              {statementLines.length ? (
                <ul id="uc3Statements" className="tbl-note">
                  {statementLines.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : (
            <p className="ad-status">{t('intel.uc3.noRankedData')}</p>
          )}
        </div>
      </div>

      {/*
       * Amendment 1 fix wave (F12): the diagnosis payload's `countries[]` — the same four
       * columns as the ranked chart, one row per programme country instead of per watershed
       * (no `rank`) — shown as its own titled block right below the ranked chart. Same
       * "designed empty state, never a silently missing block" rule as `uc3Ranked`.
       */}
      <div className="result-box" id="uc3Countries">
        <h5>{t('intel.uc3.countryRollupTitle')}</h5>
        <div className="result-pad">
          {countryRows.length ? (
            <>
              {diagnosis?.season && diagnosis.year ? (
                <p className="sub">{t('intel.uc3.rankedPeriod', { season: diagnosis.season, year: diagnosis.year })}</p>
              ) : null}
              <table className="mini" id="uc3CountriesTable">
                <thead>
                  <tr>
                    <th>{t('intel.uc3.countryRollupCountry')}</th>
                    {(['yield', ...DIAGNOSIS_FACTORS] as const).map((f) => (
                      <th className="num" key={f}>
                        {t(`intel.charts.factor.${f}`, { defaultValue: f, crop: cropLabel })} ({units[f]})
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {countryRows.map((c) => (
                    <tr key={c.unit_id}>
                      <td>{c.name}</td>
                      {(['yield', ...DIAGNOSIS_FACTORS] as const).map((f) => (
                        <td className="num" key={f}>
                          {typeof c[f] === 'number' ? fmtNum(c[f] as number, f === 'ndvi_peak' ? 2 : 1) : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <p className="ad-status">{t('intel.uc3.noCountryData')}</p>
          )}
        </div>
      </div>

      <div className="result-box" id="uc3Series">
        <h5>{t('intel.uc3.seriesTitle')}</h5>
        <div className="result-pad">
          {!selectedUnit ? (
            <p className="ad-status">{t('intel.uc3.noSeriesUnit')}</p>
          ) : series && series.ndvi.length === 0 && series.rain.length === 0 ? (
            <p className="ad-status">{t('intel.uc3.noSeriesData')}</p>
          ) : series ? (
            <ChartFrame
              title={t('intel.uc3.seriesTitle')}
              xLabel={t('intel.axis.period')}
              yLabel={t('intel.axis.ndviRain')}
              testId="uc3-series"
            >
              {() => (
                <SeriesChart
                  ndvi={series.ndvi}
                  rain={series.rain}
                  ndviLabel={t('intel.charts.ndvi')}
                  rainLabel={t('intel.charts.rain')}
                />
              )}
            </ChartFrame>
          ) : null}
        </div>
      </div>

      {diagnosis?.insight ? (
        <Insight
          insight={{
            en: safeInsightText(diagnosis.insight.en, t('intel.uc3.insightUnavailable')),
            fr: safeInsightText(diagnosis.insight.fr, t('intel.uc3.insightUnavailable', { lng: 'fr' })),
          }}
        />
      ) : null}

      {point ? (
        <div className="result-box" id="uc3UnitVsCountry">
          <h5>{point.name}</h5>
          <div className="result-pad">
            <table className="mini">
              <thead>
                <tr>
                  <th />
                  <th className="num">{point.name}</th>
                  <th className="num">{t('intel.uc3.countryMean')}</th>
                </tr>
              </thead>
              <tbody>
                {(['yield', ...DIAGNOSIS_FACTORS] as const).map((f) => (
                  <tr key={f}>
                    <td>{t(`intel.charts.factor.${f}`, { defaultValue: f, crop: cropLabel })}</td>
                    <td className="num">
                      {typeof point[f] === 'number' ? fmtNum(point[f] as number, 1) : '-'}
                    </td>
                    <td className="num">
                      {meanOf(f) != null ? fmtNum(meanOf(f) as number, 1) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Uc3;
